import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { DropzoneArea } from "material-ui-dropzone";
import React, { FC } from "react";
import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  Filter,
  FunctionField,
  List,
  ListProps,
  ReferenceInput,
  TextField,
  TextInput,
  TopToolbar,
  useDataProvider,
  useMutation,
  useNotify,
} from "react-admin";
import StatusField from "../../components/StatusField";
import { ProductInstanceIdField } from "../../components/fields";
import ProductInstanceBoard from "../ProductInstanceBoard";

const ResourceList: FC<ListProps> = (props) => {
  const [isBoardView, setIsBoardView] = React.useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = React.useState<File | null>(
    null,
  );
  const [isUploading, setIsUploading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  const [mutate] = useMutation();
  const dataProvider = useDataProvider();

  const notify = useNotify();

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    if (isUploading) {
      return;
    }
    setIsImportModalOpen(false);
    setSelectedCsvFile(null);
  };

  const handleCsvFileChange = (files: File[]) => {
    const file = files && files[0];
    console.log(file);
    setSelectedCsvFile(file || null);
  };

  const handleUploadCsv = async () => {
    if (!selectedCsvFile) {
      notify("Please select a CSV file first.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("csv", selectedCsvFile);

    setIsUploading(true);
    mutate(
      {
        type: "create",
        resource: "product-instances/import-csv",
        payload: {
          data: formData,
        },
      },
      {
        onSuccess: ({ data }: { data: any }) => {
          notify(
            "Product sets import started in background. We will notify you when it's done.",
            "info",
          );
          setIsImportModalOpen(false);
          setSelectedCsvFile(null);
          setIsUploading(false);
        },
        onFailure: (error: any) => {
          notify(`Error: ${error?.message || "Unknown error"}`, "warning");
          setIsUploading(false);
        },
      },
    );
  };

  const handleExportCsv = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    notify(
      "Product sets export started in background. We will notify you when it's ready.",
      "info",
    );

    try {
      await dataProvider.exportCsv("product-instances/export-csv");
    } catch (error: any) {
      notify(`Error: ${error?.message || "Unknown error"}`, "warning");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Box width="100%" textAlign="left">
        <Button
          variant="text"
          size="small"
          onClick={() => setIsBoardView(!isBoardView)}
        >{`Switch to ${isBoardView ? "List" : "Board"} View`}</Button>
      </Box>

      {isBoardView ? (
        <ProductInstanceBoard />
      ) : (
        <List
          {...props}
          exporter={false}
          filters={<ListFilter />}
          filter={{ "product.type": "instrument,implant" }}
          actions={
            <ProductInstanceListActions
              onOpenImportModal={handleOpenImportModal}
              onExportCsv={handleExportCsv}
              isExporting={isExporting}
              filters={<ListFilter />}
            />
          }
          bulkActionButtons={false}
          title="Product Sets"
        >
          <Datagrid>
            <ProductInstanceIdField />
            <FunctionField
              render={(record: any) => {
                return <></>;
              }}
            />
            {process.env.REACT_APP_COMPANY_CODE === "S&N" && (
              <TextField source="sap_bin_code" label="SAP Bin Code" />
            )}
            <TextField source="product.name" label="Product Name" />
            <TextField source="location.name" label="Current Location" />
            <TextField source="product.business_unit.name" label="BU" />
            <StatusField source="status" />
          </Datagrid>
        </List>
      )}

      <Dialog
        open={isImportModalOpen}
        onClose={() => {
          if (!isUploading) {
            handleCloseImportModal();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import CSV</DialogTitle>
        <DialogContent>
          <DropzoneArea
            acceptedFiles={[".csv", "text/csv"]}
            filesLimit={1}
            onChange={handleCsvFileChange}
            dropzoneText="Drag and drop a CSV file here or click"
            showPreviewsInDropzone={true}
            showAlerts={false}
            maxFileSize={20 * 1024 * 1024}
          />
          {selectedCsvFile && (
            <Box mt={2}>
              <Typography variant="body2">
                Selected file: {selectedCsvFile.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImportModal}
            color="primary"
            disabled={isUploading}
          >
            Close
          </Button>
          <Button
            onClick={handleUploadCsv}
            color="primary"
            variant="contained"
            disabled={!selectedCsvFile || isUploading}
          >
            {isUploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ResourceList;

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="Set ID" />
    <ReferenceInput
      label="Product Name"
      source="product_id"
      reference="products"
      perPage={10000}
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Current Location"
      source="location_id"
      reference="locations"
      perPage={10000}
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Business Unit"
      source="product.businessUnit.id"
      reference="business-units"
      perPage={10000}
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

interface ProductInstanceListActionsProps {
  resource?: string;
  displayedFilters?: Record<string, boolean>;
  filterValues?: Record<string, any>;
  showFilter?: (filterName: string, defaultValue: any) => void;
  onOpenImportModal: () => void;
  onExportCsv: () => void;
  isExporting: boolean;
  filters?: React.ReactElement;
  basePath?: string;
}

const ProductInstanceListActions: FC<ProductInstanceListActionsProps> = (
  props,
) => {
  const {
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    onOpenImportModal,
    onExportCsv,
    isExporting,
    filters,
    basePath,
  } = props;

  return (
    <TopToolbar>
      <CreateButton basePath={basePath} />
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onOpenImportModal}
        disabled={isExporting}
      >
        Import CSV
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onExportCsv}
        disabled={isExporting}
      >
        {isExporting ? "Exporting..." : "Export CSV"}
      </Button>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}
    </TopToolbar>
  );
};
